/* eslint-disable */
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import ClientRouteBootSkeleton from '@/components/ClientRouteBootSkeleton'
import {
  resolveClientDefaultPath,
  mergeClientPermissionCodeLists
} from '@/constants/menuPermissionMeta'
import {
  clientPathnameToRelative,
  getRequiredMenuCodesForClientPath
} from '@/utils/clientPathMenuCodeMap'
import { store } from '@/redux/store'
import { bootstrapPermissionCodes } from '@/utils/bootstrapPermissionCodes'

/**
 * `/client/*` 布局内的子路由出口守卫（挂在 `views/client/Page.js` 中，替代直接渲染 `<Outlet />`）。
 *
 * 流程简述：
 * 1. 权限接口失败：与 `ClientDefaultRedirect` 一致，跳转 `/dashboard`。
 * 2. `menuCodes` 尚未加载完成：全屏骨架（与嵌入业务首屏一致），避免未加载时误判为无权限。
 * 3. 当前路径在 `clientPathMenuCodeMap` 中无要求：直接渲染子路由（未配菜单码的页面保持原样可进）。
 * 4. 有要求：用户「菜单+模块+按钮码」并集需命中配置中的任一枚；否则 `replace` 到 `resolveClientDefaultPath(permissionCodes)`，
 *    与登录后进 `/client` 的默认落地规则一致（有 home → 指挥中心，有订单大厅码 → 订单列表，否则 dashboard）。
 *
 * `state.from` 传入 `Navigate`，便于后续若要做「无权限回退提示」时可读取来源路径。
 */
export default function ClientOutletGuard () {
  const location = useLocation()
  const {
    permissionCodes,
    permissionCodesLoaded,
    permissionCodesFetchFailed,
    permissionCodesFetching
  } = useSelector(state => state.app)

  useEffect(() => {
    if (!permissionCodesLoaded && !permissionCodesFetching && !permissionCodesFetchFailed) {
      bootstrapPermissionCodes(store)
    }
  }, [permissionCodesLoaded, permissionCodesFetching, permissionCodesFetchFailed])

  if (permissionCodesFetchFailed) {
    return <Navigate to="/dashboard" replace state={{ from: location }} />
  }

  if (!permissionCodesLoaded) {
    return <ClientRouteBootSkeleton />
  }

  const relative = clientPathnameToRelative(location.pathname)
  const required = getRequiredMenuCodesForClientPath(relative)
  // 本路径未绑定菜单码：不拦截
  if (!required || required.length === 0) {
    return <Outlet />
  }

  const userCodes = mergeClientPermissionCodeLists(permissionCodes)
  const allowed = required.some(code => userCodes.includes(code))
  if (allowed) {
    return <Outlet />
  }

  const fallback = resolveClientDefaultPath(permissionCodes)
  const next = fallback === location.pathname ? '/dashboard' : fallback
  return <Navigate to={next} replace state={{ from: location }} />
}
